// Settings: the plan of the tenant, its verified domains (ADR-0030, api.md 2.7) and its Go module paths (ADR-0038). The console has no plan numbers:
// limits come from tenant detail `limits`. There is no owner route for a plan change, so Katskill changes it.
import { useState, type FormEvent } from "react";
import type { Domain, DomainMethod, TenantDetail, TenantLimits } from "../api/types";
import { Badge } from "../components/Badge";
import { useServices } from "../components/context";
import { GoPrefixes } from "../components/GoPrefixes";
import { PendingFilter, useGrace, type PendingRow } from "../components/Grace";
import { IdCell } from "../components/IdCell";
import { CopyButton, ErrorBanner, Section, useAction } from "../components/ui";
import { useDomains } from "../components/useDomains";
import { formatTime } from "../lib/format";
import {
  countingDomains,
  DOMAIN_STATE_TEXT,
  graceEnd,
  isTenantLimits,
  lapsedDomains,
  limitText,
  planLine,
  planName,
} from "../lib/plans";
import { atLeast } from "../lib/roles";

export const ASK_PLAN_CHANGE_TEXT = "To change the plan, ask Katskill to change it.";
export const ORG_NO_DOMAIN_TEXT =
  "The Organization plan needs a verified domain. Verify a domain below, then invite members on it.";

const TENANT_GRANTS_TEXT: Record<TenantLimits["tenant_grants"], string> = {
  any: "Any tenant (read only)",
  same_domain: "Only tenants with the same verified domain (read only)",
  none: "No",
};

const METHOD_TEXT: Record<DomainMethod, string> = { sign_in: "sign-in", dns_txt: "DNS TXT" };

function TxtRecord({ d }: { d: Domain }) {
  if (d.method !== "dns_txt" || !d.txt_name || !d.txt_value) return null;
  return (
    <div className="txt-record" aria-label={`TXT record for ${d.domain}`}>
      <p className="small muted">Add this TXT record at your DNS provider, then select Verify.</p>
      <dl className="ns-facts">
        <dt>Name</dt>
        <dd>
          <code>{d.txt_name}</code> <CopyButton text={d.txt_name} label="Copy name" />
        </dd>
        <dt>Value</dt>
        <dd>
          <code>{d.txt_value}</code> <CopyButton text={d.txt_value} label="Copy value" />
        </dd>
      </dl>
    </div>
  );
}

export function Settings({ tenant }: { tenant: TenantDetail }) {
  const { api } = useServices();
  const domains = useDomains(tenant);
  const action = useAction();
  const isAdmin = atLeast(tenant.role, "admin");
  const isOwner = atLeast(tenant.role, "owner");
  const [domain, setDomain] = useState("");
  const [method, setMethod] = useState<DomainMethod>("dns_txt");
  const [added, setAdded] = useState<Domain | null>(null);

  const list = domains.data ?? [];
  const limits = isTenantLimits(tenant.limits) ? tenant.limits : null;
  const isOrg = tenant.plan === "org" || tenant.plan === "starter";
  const lapsed = lapsedDomains(list);

  const grace = useGrace<Domain>({ tenant, collection: "domains", onChange: domains.reload });
  const pendingRows: PendingRow[] = grace.items.map((d) => ({
    id: d.id,
    title: <span className="mono">{d.domain}</span>,
    detail: `${METHOD_TEXT[d.method]}, was ${d.state}`,
    deletedBy: d.deleted_by,
    deletedAt: d.deleted_at,
    purgeAfter: d.purge_after,
    purge: { id: d.id, title: `domain ${d.domain}`, confirm: d.domain, confirmLabel: "domain" },
  }));

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    const value = domain.trim().toLowerCase();
    if (!value) return;
    const ok = await action.run(async () => {
      const d = await api.addDomain(tenant.slug, { domain: value, method });
      setAdded(d.method === "dns_txt" ? d : null);
    });
    if (ok) {
      setDomain("");
      domains.reload();
    }
  }

  async function onVerify(d: Domain) {
    const ok = await action.run(async () => {
      const r = await api.verifyDomain(tenant.slug, d.id);
      if (r.state === "verified") setAdded(null);
    });
    if (ok) domains.reload();
  }

  return (
    <>
      {isOwner &&
        lapsed.map((d) => (
          <p key={d.id} className="banner warn" role="status">
            The domain {d.domain} lapsed. It still counts until {formatTime(graceEnd(d)).slice(0, 10)}. Verify
            it again before then, or the plan rules apply without it.
          </p>
        ))}
      <Section title="Plan">
        <p>
          <strong>{planLine(tenant)}</strong>
        </p>
        {isOrg && countingDomains(list).length === 0 && (
          <p className="banner warn" role="status">
            {ORG_NO_DOMAIN_TEXT}
          </p>
        )}
        {limits && (
          <table className="data-table plan-limits">
            <caption className="muted small">Limits of the {planName(limits.plan)} plan</caption>
            <tbody>
              <tr>
                <th scope="row">Members</th>
                <td>{limitText(limits.members)}</td>
              </tr>
              <tr>
                <th scope="row">Principals</th>
                <td>{limitText(limits.principals)}</td>
              </tr>
              <tr>
                <th scope="row">Namespaces</th>
                <td>{limitText(limits.namespaces)}</td>
              </tr>
              <tr>
                <th scope="row">Storage</th>
                <td>{limitText(limits.storage_bytes, true)}</td>
              </tr>
              <tr>
                <th scope="row">Grants to other tenants</th>
                <td>{TENANT_GRANTS_TEXT[limits.tenant_grants]}</td>
              </tr>
            </tbody>
          </table>
        )}
        <p className="muted">{ASK_PLAN_CHANGE_TEXT}</p>
      </Section>

      <Section
        title="Domains"
        actions={<PendingFilter view={grace.view} setView={grace.setView} count={grace.count} />}
      >
        <p className="muted">
          A verified domain is an email domain that this tenant controls. The Organization plan uses it for
          members and for grants to other tenants.
        </p>
        <ErrorBanner error={domains.error ?? action.error ?? grace.error} />
        {grace.view === "pending" ? (
          grace.rowsTable(pendingRows)
        ) : (
          <>
            <table className="stack data-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Method</th>
                  <th>State</th>
                  <th>Verified</th>
                  <th>Grace ends</th>
                  <th>ID</th>
                  <th className="actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((d) => (
                  <tr key={d.id}>
                    <td className="mono">{d.domain}</td>
                    <td data-label="Method">
                      <Badge kind={d.method} />
                    </td>
                    <td data-label="State" title={DOMAIN_STATE_TEXT[d.state]}>
                      <span
                        className={`tag ${d.state === "verified" ? "ok" : d.state === "pending" ? "" : "expired"}`}
                      >
                        {d.state}
                      </span>
                    </td>
                    <td data-label="Verified">{formatTime(d.verified_at, "not yet")}</td>
                    <td data-label="Grace ends">{d.state === "lapsed" ? formatTime(graceEnd(d)) : ""}</td>
                    <IdCell id={d.id} />
                    <td className="actions" data-label="Actions">
                      {isAdmin && (
                        <>
                          {d.state !== "verified" && (
                            <button
                              type="button"
                              className="secondary"
                              disabled={action.busy}
                              onClick={() => void onVerify(d)}
                            >
                              Verify
                            </button>
                          )}
                          <button
                            type="button"
                            className="danger"
                            disabled={action.busy || grace.busy}
                            onClick={() =>
                              grace.askDelete({
                                id: d.id,
                                title: `domain ${d.domain}`,
                                message:
                                  "It stops counting at once. Members not on another verified domain and grants that need it are affected; the list shows after the delete.",
                                typedName: d.domain,
                                typedLabel: "domain",
                                confirmParam: d.domain,
                              })
                            }
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {domains.data && list.length === 0 && <p className="muted">No domains yet.</p>}
            {list
              .filter((d) => d.state === "pending" || d.id === added?.id)
              .map((d) => (
                <TxtRecord key={d.id} d={d.id === added?.id ? { ...d, ...added } : d} />
              ))}
          </>
        )}
      </Section>
      {grace.ui}

      {isAdmin && (
        <Section title="Add a domain">
          <form onSubmit={onAdd} className="form">
            <label>
              Domain
              <input
                required
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="acme.com"
                autoComplete="off"
              />
              <small className="muted">Public email domains (gmail.com and others) cannot be verified.</small>
            </label>
            <fieldset>
              <legend>Method</legend>
              <label className="check">
                <input
                  type="radio"
                  name="method"
                  checked={method === "dns_txt"}
                  onChange={() => setMethod("dns_txt")}
                />{" "}
                DNS TXT record: any sign-in method
              </label>
              <label className="check">
                <input
                  type="radio"
                  name="method"
                  checked={method === "sign_in"}
                  onChange={() => setMethod("sign_in")}
                />{" "}
                Sign-in: the exact domain of your email, signed in with Google or Microsoft
              </label>
            </fieldset>
            <button type="submit" disabled={action.busy}>
              Add domain
            </button>
          </form>
        </Section>
      )}

      <GoPrefixes tenant={tenant} domains={list} />
    </>
  );
}
